import { api } from "@/app/services/api.service";

export const URA_KEYS_PATH = "configuration/integration/ura-keys";
export const URA_KEYS_AUDIT_PATH = "configuration/integration/ura-keys/audit";

export type UraIntegrationKeys = {
  consumerKey: string | null;
  consumerSecret: string | null;
  configured: boolean;
};

export function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data") &&
    data.data != null
  ) {
    return data.data;
  }
  return data;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

export function parseUraIntegrationKeys(body: unknown): UraIntegrationKeys {
  if (body == null || typeof body !== "object") {
    return { consumerKey: null, consumerSecret: null, configured: false };
  }
  const record = body as Record<string, unknown>;
  const consumerKey = readString(record, ["consumerKey", "consumer_key", "key"]);
  const consumerSecret = readString(record, ["consumerSecret", "consumer_secret", "secret"]);
  const configured =
    record.configured === true ||
    record.hasKeys === true ||
    (consumerKey != null && consumerKey.length > 0);

  return {
    consumerKey,
    consumerSecret,
    configured,
  };
}

export async function fetchUraIntegrationKeys(): Promise<UraIntegrationKeys> {
  const res = await api().get(URA_KEYS_PATH);
  return parseUraIntegrationKeys(unwrapPayload(res));
}

export async function saveUraIntegrationKeys(payload: {
  consumerKey: string;
  consumerSecret: string;
}) {
  await api().post(URA_KEYS_PATH, payload);
}

export async function deleteUraIntegrationKeys() {
  await api().delete(URA_KEYS_PATH);
}

export function extractAuditList(body: unknown): Record<string, unknown>[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body.filter((item) => item != null && typeof item === "object") as Record<string, unknown>[];
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.auditLogs, obj.logs, obj.items, obj.content, obj.records, obj.data];
  for (const candidate of candidates) {
    const parsed = extractAuditList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchUraKeysAudit(): Promise<Record<string, unknown>[]> {
  const res = await api().get(URA_KEYS_AUDIT_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractAuditList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractAuditList(rootObj.data);
    const fromUnwrapped = extractAuditList(unwrapPayload(res));
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractAuditList(root);
  }
  return [];
}
